"use client";

import { useEffect, useMemo } from "react";
import { FEATURE_FLAGS, METRICS_EVENTS, StreakSummaryResponseV2 } from "@cede/types";
import { CancelledStreakNotificationItem } from "./CancelledStreakNotificationItem";
import { NotificationBell } from "./NotificationBell";
import type { CampaignType } from "./NotificationItem";
import { NotificationList } from "./NotificationList";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { PortalModal } from "@/components/ui/portal-modal";
import { useMetricsEvents } from "@/hooks/data/useMetricsEvents";
import { useFeatureFlagIsOn } from "@/hooks/feature-flags";
import { useRecentlyCancelledStreaks, useWhitelistedCampaigns } from "@/hooks/notifications";
import { useDevice } from "@/hooks/ui/useDevice";
import { useNotificationState } from "@/store/notificationStore";
import { useRouter } from "next/navigation";

interface NotificationDropdownProps {
  className?: string;
}

/**
 * NotificationDropdown - Dropdown/PortalModal for displaying exclusive campaign notifications.
 *
 * Features:
 * - Uses Popover on desktop, PortalModal on mobile
 * - Displays list of whitelisted exclusive campaigns
 * - Displays recently cancelled autopilot streaks due to config updates
 * - Allows dismissing individual campaigns
 * - Tracks Mixpanel events for all interactions
 * - Marks campaigns as seen when opened
 */
export const NotificationDropdown = ({ className }: NotificationDropdownProps) => {
  const router = useRouter();
  const { isMobile } = useDevice();
  const { trackEvent } = useMetricsEvents();
  const { whitelistedCampaigns } = useWhitelistedCampaigns();
  const { cancelledStreaks } = useRecentlyCancelledStreaks();
  const { isDropdownOpen, setDropdownOpen, dismissCampaign, markAllAsSeen, getVisibleCampaignIds, hasUnseenCampaigns } =
    useNotificationState();
  const isNotificationsEnabled = useFeatureFlagIsOn(FEATURE_FLAGS.EXCLUSIVE_NOTIFICATIONS_HUB_ENABLE);

  const campaignIds = useMemo(() => whitelistedCampaigns.map((c) => String(c.id)), [whitelistedCampaigns]);
  const visibleCampaignIds = getVisibleCampaignIds(campaignIds);
  const visibleCampaigns = whitelistedCampaigns.filter((c) => visibleCampaignIds.includes(String(c.id)));

  const cancelledStreakIds = useMemo(() => cancelledStreaks.map((s) => s.id), [cancelledStreaks]);
  const visibleCancelledStreakIds = getVisibleCampaignIds(cancelledStreakIds);
  const visibleCancelledStreaks = cancelledStreaks.filter((s) => visibleCancelledStreakIds.includes(s.id));

  const allNotificationIds = useMemo(() => [...campaignIds, ...cancelledStreakIds], [campaignIds, cancelledStreakIds]);
  const hasUnseen = hasUnseenCampaigns(allNotificationIds);
  const totalVisibleCount = visibleCampaigns.length + visibleCancelledStreaks.length;

  // Mark all as seen when dropdown opens
  useEffect(() => {
    if (isDropdownOpen && allNotificationIds.length > 0 && hasUnseen) {
      markAllAsSeen(allNotificationIds);
    }
  }, [isDropdownOpen, allNotificationIds, hasUnseen, markAllAsSeen]);

  // Don't render if disabled
  if (!isNotificationsEnabled) {
    return null;
  }

  const handleOpenChange = (open: boolean) => {
    setDropdownOpen(open);
  };

  const handleBellClick = () => {
    setDropdownOpen(true);
  };

  const handleDismiss = (campaignId: string) => {
    const campaign = whitelistedCampaigns.find((c) => String(c.id) === campaignId);
    dismissCampaign(campaignId);
    trackEvent(METRICS_EVENTS.EXCLUSIVE_NOTIFICATION_CAMPAIGN_DISMISSED, {
      campaignId,
      campaignName: campaign?.name,
    });
  };

  const handleCancelledStreakDismiss = (streakId: string) => {
    dismissCampaign(streakId);
  };

  const handleCampaignClick = (campaign: CampaignType) => {
    trackEvent(METRICS_EVENTS.EXCLUSIVE_NOTIFICATION_CAMPAIGN_CLICKED, {
      campaignId: String(campaign.id),
      campaignName: campaign.name,
      exchange: campaign.exchangeIds?.[0],
    });
    setDropdownOpen(false);
    router.push(`/${campaign.name}`);
  };

  const handleCancelledStreakClick = (streak: StreakSummaryResponseV2) => {
    setDropdownOpen(false);
    router.push(`/${streak.campaignName}`);
  };

  const dropdownContent = (showHeader: boolean, showCount: boolean) => (
    <div className="flex flex-col">
      {showHeader && (
        <div className="mb-3 flex items-center justify-between">
          <h3 className="subtitle2 text-cede-text-primary">Notifications</h3>
          {showCount && totalVisibleCount > 0 && (
            <span className="badge rounded-full bg-cede-primary/20 px-2 py-0.5 text-cede-primary">
              {totalVisibleCount}
            </span>
          )}
        </div>
      )}
      {visibleCancelledStreaks.length > 0 && (
        <div className="mb-2 flex flex-col gap-2">
          {visibleCancelledStreaks.map((streak) => (
            <CancelledStreakNotificationItem
              key={streak.id}
              streak={streak}
              onDismiss={handleCancelledStreakDismiss}
              onClick={handleCancelledStreakClick}
            />
          ))}
        </div>
      )}
      <NotificationList campaigns={visibleCampaigns} onDismiss={handleDismiss} onClick={handleCampaignClick} />
    </div>
  );

  // Mobile: Use PortalModal
  if (isMobile) {
    return (
      <div className={className}>
        <NotificationBell onClick={handleBellClick} />
        <PortalModal open={isDropdownOpen} onOpenChange={handleOpenChange} className="w-full max-h-[80vh]">
          <PortalModal.Header>
            <PortalModal.Title>Notifications</PortalModal.Title>
          </PortalModal.Header>
          <PortalModal.Content scrollable className="px-2 pb-4">
            {dropdownContent(false, false)}
          </PortalModal.Content>
        </PortalModal>
      </div>
    );
  }

  // Desktop: Use Popover
  return (
    <Popover open={isDropdownOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <div className={className}>
          <NotificationBell onClick={handleBellClick} />
        </div>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-4" sideOffset={8}>
        <div className="max-h-[40vh] overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-cede-border/40 scrollbar-track-transparent hover:scrollbar-thumb-cede-border/60">
          {dropdownContent(true, true)}
        </div>
      </PopoverContent>
    </Popover>
  );
};
